'use client'

import { useEffect, useRef, useCallback } from 'react'
import { usePathname } from 'next/navigation'
import Lenis from 'lenis'

interface LenisProviderProps {
  children: React.ReactNode
}

// Global Lenis instance for external access
let globalLenis: Lenis | null = null

// Helper function to get Lenis instance
export const getLenisInstance = (): Lenis | null => globalLenis

// Helper function to refresh Lenis (useful after dynamic content loads)
export const refreshLenis = () => {
  if (globalLenis) {
    globalLenis.resize()
  }
}

// Helper function to scroll with Lenis
export const scrollToWithLenis = (target: number | HTMLElement, options?: { offset?: number; duration?: number }) => {
  if (globalLenis) {
    globalLenis.scrollTo(target, {
      offset: options?.offset || 0,
      duration: options?.duration || 1.2,
      easing: (t: number) => Math.min(1, 1.001 - Math.pow(2, -10 * t))
    })
    return true
  }
  return false
}

export default function LenisProvider({ children }: LenisProviderProps) {
  const lenisRef = useRef<Lenis | null>(null)
  const rafRef = useRef<number | null>(null)
  const isInitializedRef = useRef(false)
  const pathname = usePathname()

  // Improved desktop detection with caching
  const isDesktop = useCallback(() => {
    if (typeof window === 'undefined') return false
    
    // Check if it's a touch device
    const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0
    
    // Check screen width (desktop typically > 1024px)
    const isLargeScreen = window.innerWidth > 1024
    
    // Check user agent for mobile indicators
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
    
    return !isTouchDevice && isLargeScreen && !isMobile
  }, [])

  const shouldUseLenis = useCallback(() => {
    const isAdminRoute = pathname.startsWith('/admin') || pathname.startsWith('/login')
    return !isAdminRoute && isDesktop()
  }, [isDesktop, pathname])

  // Initialize Lenis
  const initializeLenis = useCallback(() => {
    if (lenisRef.current || !shouldUseLenis() || isInitializedRef.current) return

    try {
      // Add CSS classes to html element
      document.documentElement.classList.add('lenis')
      
      // Initialize Lenis with improved settings
      lenisRef.current = new Lenis({
        duration: 1.2,
        easing: (t: number) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
        orientation: 'vertical',
        gestureOrientation: 'vertical',
        smoothWheel: true,
        wheelMultiplier: 1,
        touchMultiplier: 2,
        syncTouch: false,
        infinite: false,
        autoRaf: false,
        // Add these for better stability
        wrapper: window,
        content: document.documentElement,
      })

      // Set global reference
      globalLenis = lenisRef.current
      isInitializedRef.current = true

      // Improved animation frame loop with error handling
      const raf = (time: number) => {
        try {
          if (lenisRef.current) {
            lenisRef.current.raf(time)
          }
          rafRef.current = requestAnimationFrame(raf)
        } catch (error) {
          console.warn('Lenis RAF error:', error)
          // Try to recover by reinitializing
          if (rafRef.current) {
            cancelAnimationFrame(rafRef.current)
            rafRef.current = null
          }
          setTimeout(() => {
          if (shouldUseLenis()) {
              initializeLenis()
            }
          }, 100)
        }
      }

      rafRef.current = requestAnimationFrame(raf)

      // Listen for content changes to refresh Lenis
      const refreshOnContentChange = () => {
        if (lenisRef.current) {
          lenisRef.current.resize()
        }
      }

      // Refresh when images load
      const handleImageLoad = () => refreshOnContentChange()
      
      // Add event listeners
      window.addEventListener('load', refreshOnContentChange)
      document.addEventListener('DOMContentLoaded', refreshOnContentChange)
      
      // Listen for image loads globally
      const images = document.querySelectorAll('img')
      images.forEach(img => {
        if (img.complete) {
          refreshOnContentChange()
        } else {
          img.addEventListener('load', handleImageLoad)
          img.addEventListener('error', handleImageLoad) // Also refresh on error
        }
      })

      // Cleanup function for event listeners
      const cleanup = () => {
        window.removeEventListener('load', refreshOnContentChange)
        document.removeEventListener('DOMContentLoaded', refreshOnContentChange)
        images.forEach(img => {
          img.removeEventListener('load', handleImageLoad)
          img.removeEventListener('error', handleImageLoad)
        })
      }

      return cleanup
    } catch (error) {
      console.error('Failed to initialize Lenis:', error)
      isInitializedRef.current = false
      return undefined
    }
  }, [shouldUseLenis])

  // Destroy Lenis
  const destroyLenis = useCallback(() => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current)
      rafRef.current = null
    }

    if (lenisRef.current) {
      try {
        lenisRef.current.destroy()
      } catch (error) {
        console.warn('Error destroying Lenis:', error)
      }
      lenisRef.current = null
      globalLenis = null
    }

    isInitializedRef.current = false
    document.documentElement.classList.remove('lenis')
  }, [])

  useEffect(() => {
    let cleanup: (() => void) | undefined

    if (shouldUseLenis()) {
      cleanup = initializeLenis()
    } else {
      destroyLenis()
    }

    // Handle resize events to re-evaluate desktop detection
    const handleResize = () => {
      if (!shouldUseLenis() && lenisRef.current) {
        destroyLenis()
      } else if (shouldUseLenis() && !lenisRef.current) {
        cleanup = initializeLenis()
      }
    }

    window.addEventListener('resize', handleResize)

    // Handle visibility change to restart Lenis if needed
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Page is hidden, pause Lenis
        if (lenisRef.current) {
          lenisRef.current.stop()
        }
      } else {
        // Page is visible, resume Lenis
        if (lenisRef.current) {
          lenisRef.current.start()
          // Refresh to recalculate layout
          setTimeout(() => refreshLenis(), 100)
        } else if (shouldUseLenis()) {
          // Reinitialize if needed
          cleanup = initializeLenis()
        }
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      window.removeEventListener('resize', handleResize)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      cleanup?.()
      destroyLenis()
    }
  }, [shouldUseLenis, initializeLenis, destroyLenis])

  return <>{children}</>
}
